import styled from 'styled-components';
import constants from '../constants';
import React, { useContext, useRef } from 'react';
import { EditorContext, EditorStateContext } from '../../../utils/contexts';
import { useHistory } from 'react-router-dom';
import TextWithLoader from '../../../shared/TextWithLoader';
import HelpDialog from '../../../shared/HelpDialog';
import { useSnackbar } from 'notistack';
import { withTracking } from '../../../shared/Tracking';
import { FormControl, TextField } from '@mui/material';
import useAPI from '../../../utils/api';
import { useRoleCheck } from '../../../utils/useRoleCheck';
import DOMPurify from 'dompurify';

const NameField = styled.div`
  gap: 1em;
  margin-left: 15px;
  grid-template-columns: 150px 1fr;
`;

const HelpButton = styled.div`
  text-align: right;

  a {
    display: inline-block;
    color: #27ab9e;
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const ActionButtons = styled.div`
  margin-right: 1em;
  align-items: baseline;
  display: inline-flex;
  justify-content: right;
  gap: 1em;
  white-space: nowrap;
`;

const CancelButton = styled.a`
  display: inline-block;
  color: #27ab9e;
`;

const SaveAndPauseButton = styled.a`
  display: inline-block;
  color: #27ab9e;
`;

const SaveButton = styled.a`
  background-color: #27ab9e;
  color: white;
  border-radius: 5px;
  padding: 8px 17px;
`;

function TopBarNavigation({ className, mode, setMode, trackEvent }) {
  const API = useAPI();
  const history = useHistory();
  const { settings, systemUpdate, flushAll } = useContext(EditorContext);
  const editor = useContext(EditorStateContext);
  const { enqueueSnackbar } = useSnackbar();
  const handleRoleCheck = useRoleCheck(API.currentRole);
  const isPublishingRef = useRef(false);

  const save = async (settings, { pause }) => {
    // Only block if a save/publish is already in progress according to editor state.
    // isPublishingRef may be set by onPublish before calling flushAll.
    if (editor.state.publishing || editor.state.saving) {
      return;
    }
    isPublishingRef.current = true;

    try {
      const isForm = settings.element_subtype === 'forms'
      const saveFn = isForm ? editor.state.api.saveForm : editor.state.api.savePopup
      const [ok, response] = await saveFn(settings, { pause });
      if (ok) {
        try {
          const savedEvent = isForm
            ? (pause ? 'Form Saved as Draft' : 'Form Published')
            : (pause ? 'Popup Saved as Draft' : 'Popup Published');
          window.analytics?.track(savedEvent, {
            goal: settings.element_subtype,
            popup_type: settings.type,
            ...(isForm && { form_id: response?.form?.id || settings.form_id }),
            site_id: editor.state.site?.id,
            is_new: !settings.id,
            is_ab_test: settings.is_ab_test || false,
            is_campaign_scheduled: Boolean(settings.campaign_id),
            campaign_id: settings.campaign_id || null,
            ...(settings.element_subtype === 'gamification' && {
              game_type: settings.gamification?.game_type,
              reward_count: settings.gamification?.rewards?.length,
              collect_email_before: settings.gamification?.collect_email_before,
            }),
          });
          if (!pause && settings.copyId) {
            window.analytics?.track(isForm ? 'Form AB Test Created' : 'AB Test Created', {
              goal: settings.element_subtype,
              popup_type: settings.type,
              site_id: editor.state.site?.id,
              copy_of: settings.copyId,
            });
          }
        } catch (_) {}
        enqueueSnackbar(
          (isForm ? 'Form' : 'Popup') + ' is' + `${pause ? ' saved' : ' created'}` + ' successfully',
          { variant: 'success' }
        );
        editor.dispatch({
          type: 'EDITOR.SAVING_DONE',
          payload: { systemUpdate, response },
        });
        if (!pause) {
          history.push(`/sites/${editor.state.site.id}/${isForm ? 'forms' : 'manage'}`);
        }
      } else {
        enqueueSnackbar(response?.message || response.error, {
          variant: 'error',
        });
        editor.dispatch({
          type: 'EDITOR.SAVING_ERROR',
          payload: response.message,
        });
      }
    } catch (error) {
      enqueueSnackbar(`Couldn't save popup. ${error.message}`, {
        variant: 'error',
      });
      editor.dispatch({ type: 'EDITOR.SAVING_ERROR', payload: error });
    } finally {
      isPublishingRef.current = false;
    }
  };

  const validate = () => {
    if (
      settings.success_script !== undefined &&
      settings.success_script !== null
    ) {
      const success_script = settings.success_script;
      let hasError = false;
      let errorMsg;

      // Sanitize input using DOMPurify
      const sanitizedInput = DOMPurify.sanitize(success_script);

      // Step 2: Check if sanitized input is different from the original input
      if (sanitizedInput !== success_script) {
        errorMsg = 'Invalid success script!';
        hasError = true;
      }

      // Step 3: Check for external content patterns
      const externalPattern = /<script\s+src=|<iframe\s+src=/gi;
      if (externalPattern.test(success_script)) {
        errorMsg = 'Invalid success script!';
        hasError = true;
      }

      // Step 4: Check for disallowed JavaScript functions/objects
      const disallowedPatterns =
        /(document\.head|document\.body|document\.createElement)/gi;
      if (disallowedPatterns.test(success_script)) {
        errorMsg = 'Invalid success script!';
        hasError = true;
      }

      // If all checks pass, the input is considered safe
      if (hasError) {
        enqueueSnackbar(`${errorMsg}`, { variant: 'error' });
        history.push(
          `/sites/${editor.state.site.id}/popups/${
            settings.id || 'new'
          }/conversion`
        );
        return false;
      }
    }
    const formCollectsContactInfo =
      settings.subtype !== 'forms' ||
      (settings.form_questions || []).some((q) =>
        ['email', 'phone'].includes(q.question_type)
      );

    if (
      (settings.subtype === constants.SUBTYPES.EMAIL ||
        settings.subtype === constants.SUBTYPES.GAMIFICATION ||
        settings.subtype === 'forms') &&
      !settings.contact_list_id &&
      formCollectsContactInfo
    ) {
      enqueueSnackbar(`Please choose a contact list`, { variant: 'error' });
      const basePath = settings.subtype === 'forms' ? 'forms' : 'popups';
      history.push(
        `/sites/${editor.state.site.id}/${basePath}/${
          settings.id || 'new'
        }/conversion`
      );
      return false;
    }

    if (
      settings.type === constants.TYPES.INLINE &&
      !settings.inline_css_selector &&
      editor.state.site.partner !== 'shopify'
    ) {
      enqueueSnackbar(
        `Please enter a css selector to insert the inline element`,
        { variant: 'error' }
      );
      const inlineBasePath = settings.subtype === 'forms' ? 'forms' : 'popups';
      history.push(
        `/sites/${editor.state.site.id}/${inlineBasePath}/${settings.id || 'new'}/design`
      );
      return false;
    }

    if (
      settings.subtype === constants.SUBTYPES.EMAIL &&
      settings.settings.fields_to_collect
    ) {
      const fields = settings.settings.fields_to_collect;
      const custom_fields = fields.filter((f) => {
        return f.id.startsWith('field_');
      });
      let nameLabelError = false;
      let emptyOptionsError = false;
      custom_fields.forEach((cf) => {
        if (!cf.name) {
          nameLabelError = true;
          return;
        }

        if (
          (cf.type === 'multi_select' || cf.type === 'select') &&
          !cf.options?.trim()
        ) {
          emptyOptionsError = true;
          return;
        }
      });

      fields.forEach((f) => {
        if (!f.label) {
          nameLabelError = true;
          return;
        }
      });

      if (nameLabelError) {
        enqueueSnackbar(`Please enter name and label for each input field`, {
          variant: 'error',
        });
        history.push(
          `/sites/${editor.state.site.id}/popups/${settings.id || 'new'}/design`
        );
        return false;
      }

      if (emptyOptionsError) {
        enqueueSnackbar(
          `Please enter options for Dropdown and Multi-select type input field`,
          {
            variant: 'error',
          }
        );
        history.push(
          `/sites/${editor.state.site.id}/popups/${settings.id || 'new'}/design`
        );
        return false;
      }
    }

    if (
      settings.subtype === constants.SUBTYPES.CALL &&
      !settings.phone_number
    ) {
      enqueueSnackbar(`Please enter a valid phone number`, {
        variant: 'error',
      });
      history.push(
        `/sites/${editor.state.site.id}/popups/${settings.id || 'new'}/design`
      );
      return false;
    }

    if (settings.view_condition === 'event.') {
      enqueueSnackbar(
        `Please enter the event name for the custom event trigger`,
        { variant: 'error' }
      );
      history.push(
        `/sites/${editor.state.site.id}/popups/${settings.id || 'new'}/trigger`
      );
      return false;
    }

    if (settings.view_condition === 'onclick' && !settings.click_selector) {
      enqueueSnackbar(
        `Please enter the css selector of the element for the onclick event trigger`,
        { variant: 'error' }
      );
      history.push(
        `/sites/${editor.state.site.id}/popups/${settings.id || 'new'}/trigger`
      );
      return false;
    }

    if (settings.view_condition === 'onclick') {
      try {
        document.createDocumentFragment().querySelector(settings.click_selector);
      } catch (_) {
        enqueueSnackbar(
          `The css selector of the element for the onclick event trigger is not valid`,
          { variant: 'error' }
        );
        history.push(
          `/sites/${editor.state.site.id}/popups/${settings.id || 'new'}/trigger`
        );
        return false;
      }
    }

    return true;
  };

  const onCancel = () => {
    editor.dispatch({ type: 'EDITOR.CANCEL', payload: { history } });
  };

  const onSaveAndPause = () => {
    // To check allowed role to perform action
    if (handleRoleCheck()) {
      return;
    }
    if (!validate()) {
      return;
    }
    // Flush pending inputs, merge into settings before saving
    (async () => {
      if (isPublishingRef.current) return;
      isPublishingRef.current = true;
      try {
        const pendingPartials = flushAll ? await flushAll() : {};
        const settingsToSave = { ...settings, ...pendingPartials };
        editor.dispatch({
          type: 'EDITOR.SAVE',
          payload: { settings: settingsToSave, editor, systemUpdate },
        });
        await save(settingsToSave, { pause: true });
      } finally {
        isPublishingRef.current = false;
      }
    })();
  };

  const onPublish = async () => {
    // To check allowed role to perform action
    if (handleRoleCheck()) {
      return;
    }
    if (!validate()) {
      return;
    }

    if (isPublishingRef.current) return;
    isPublishingRef.current = true;
    try {
      const pendingPartials = flushAll ? await flushAll() : {};
      const settingsToSave = { ...settings, ...pendingPartials };
      editor.dispatch({
        type: 'EDITOR.PUBLISH',
        payload: { settings: settingsToSave, editor, history },
      });
      await save(settingsToSave, { pause: false });
    } finally {
      isPublishingRef.current = false;
    }
  };

  const handleNameChange = (e) => {
    systemUpdate({ name: e.target.value });
  };

  // A popup attached to a campaign has its live/paused state controlled
  // solely by that campaign's own schedule - Save & Pause vs Publish Now
  // would otherwise fight the campaign for control of paused_at (see
  // savePopup in utils/api.js). Collapse to one action that's honest about
  // what it actually does in that case: save the config, let the campaign
  // decide when it's actually live.
  const hasCampaign = Boolean(settings.campaign_id);

  return (
    <nav className={className}>
      <NameField>
        <FormControl>
          <TextField
            label="Name"
            variant="outlined"
            size="small"
            type="text"
            value={settings.name}
            onChange={(e) => handleNameChange(e)}
          />
        </FormControl>
      </NameField>
      <Container>
        <ActionButtons>
          <HelpButton>
            <HelpDialog isOpen={false} buttonText={'Help?'} showIcon={false} />
          </HelpButton>
          <CancelButton onClick={onCancel}>
            {settings.userEdited ? 'Cancel' : 'Close'}
          </CancelButton>
          {!hasCampaign && (
            <SaveAndPauseButton onClick={onSaveAndPause}>
              <TextWithLoader loading={editor.state.saving} color="#27ab9e">
                Save & Pause
              </TextWithLoader>
            </SaveAndPauseButton>
          )}
          <SaveButton onClick={onPublish}>
            <TextWithLoader loading={editor.state.publishing}>
              {hasCampaign ? 'Save & Schedule' : 'Publish Now'}
            </TextWithLoader>
          </SaveButton>
        </ActionButtons>
      </Container>
    </nav>
  );
}

export default styled(withTracking(TopBarNavigation))`
  height: 60px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #8f8f8f;
  background-color: white;
  box-shadow: 0px 0 3px 1px #8f8f8f;
`;
